import { useEffect, useMemo, useState } from "react";

import uniq from "lodash/uniq";

import DurationService from "app/services/DurationService";
import useActivationObjectQuery from "app/pages/activation/queries/data/useActivationObjectQuery";

import { SORT_ITEMS } from "../../components/Topics/constants";
import { PRELOADER_COUNT } from "../../components/Topics/Topic/constants";
import type { GetDataSchema } from "../../components/Topics/types";
import { TopicFilters } from "../../components/Topics/types";
import { normalizeResponse } from "../../helpers/topics/dataGenerators";
import { filterTopics, getFilterDisabledFlags, sortTopics } from "../../helpers/topics/logic";
import { useGetTopicsDataQuery } from "../../queries/topics/useGetTopicsDataQuery";
import { useTopicSuggestions } from "../../queries/topics/useTopicSuggestions";
import { useIconsQuery } from "../../queries/useIconsQuery";
import type { BenchmarkGranularity } from "../../types/common";

type UseTopicsSchema = {
    activationId: string;
    duration: string;
    granularity: BenchmarkGranularity;
    isTopicsCreated: boolean;
    createdTopicsLength: number;
};

export const useTopicsData = ({ activationId, duration, granularity, isTopicsCreated }: UseTopicsSchema) => {
    const [filter, setFilter] = useState(TopicFilters.All);
    const [sorting, setSorting] = useState(SORT_ITEMS[0]);
    const { data: activationData } = useActivationObjectQuery(activationId);

    const existingTopicsLength = activationData?.assets.topics?.length ?? 0;

    const {
        forAPI: { from, to },
    } = DurationService.getDurationData(duration);

    const startDate = from.replaceAll("|", ".");
    const endDate = to.replaceAll("|", ".");

    const [isShouldFetchTopics, setIsShouldFetchTopics] = useState(false);

    useEffect(() => {
        if (activationId && isTopicsCreated) {
            setIsShouldFetchTopics(true);
        }
    }, [activationId, isTopicsCreated]);

    const {
        data: suggestions,
        isLoading: isSuggestionsDataLoading,
        fetchStatus: suggestionsFetchStatus,
    } = useTopicSuggestions(activationId, !isTopicsCreated);

    const {
        data: topics,
        isLoading,
        error,
        fetchStatus: topicsFetchStatus,
    } = useGetTopicsDataQuery({ activationId, startDate, endDate, granularity }, isShouldFetchTopics);

    const leaderDomainList = topics && uniq(topics.map(({ leader, primary }) => leader?.domain ?? primary?.domain));

    const { data: icons } = useIconsQuery(leaderDomainList, !!leaderDomainList);

    const sortedData = useMemo(() => {
        if (!topics || !topics.length) {
            return [];
        }

        const normalized = normalizeResponse({ topicsRaw: topics, icons, granularity });

        if (sorting) {
            normalized.sort(sortTopics(sorting.id));
        }

        return normalized;
    }, [topics, icons, granularity, sorting]);

    const flags = useMemo(() => getFilterDisabledFlags(sortedData), [sortedData]);

    const filteredData = useMemo(() => {
        if (!sortedData) {
            return [];
        }

        return filterTopics(filter, sortedData);
    }, [sortedData, filter]);

    const getData = (): GetDataSchema[] => {
        if (filteredData.length) {
            return filteredData;
        }

        if (Array.isArray(suggestions) && suggestions.length >= PRELOADER_COUNT && !existingTopicsLength) {
            return suggestions;
        }

        return new Array(existingTopicsLength).fill({ topicName: null }) as unknown as { topicName: null }[];
    };

    const isTopicsLoading = isLoading && topicsFetchStatus !== "idle";
    const isSuggestionsLoading = isSuggestionsDataLoading && suggestionsFetchStatus !== "idle";

    return {
        data: getData(),
        isLoading: isTopicsLoading || isSuggestionsLoading,
        isDisabled: !isTopicsCreated || isLoading,
        error,
        flags,
        filter,
        sorting,
        createdTopics: topics,
        setFilter,
        setSorting,
    };
};
